import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
} from 'shared/api/APIQuery';
import { requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { TEN_MINUTES } from 'shared/constants/NumberConstants';
import {
  NotificationType,
  NotificationVariant,
} from 'shared/layout/notification-stack/data/NotificationDTO';
import { useFlag } from 'shared/settings/FeatureToggles';

const SUBSCRIPTION_PAYMENT_NOTIFICATIONS = new Set<NotificationType>([
  'subscription_payment_failed',
  'subscription_card_expiring',
]);

export function useNotifications({
  staleTime = TEN_MINUTES,
  ...options
}: APIQueryOptions<NotificationVariant[]> = {}): APIQueryResult<
  NotificationVariant[]
> {
  const select = useSubscriptionPaymentNotificationsSelect();

  return useAPIQuery(
    ['notifications'],
    () => requestCarrierAPI('GET /internal/web/notifications/'),
    { ...options, staleTime, select },
  );
}

type NotificationOfType<TType extends NotificationType> = Extract<
  NotificationVariant,
  { notification_type: TType }
>;

export function useNotification<TType extends NotificationType>(
  type: TType,
): NotificationOfType<TType> | undefined {
  const { data: notifications } = useNotifications();

  return notifications?.find(
    (notification): notification is NotificationOfType<TType> =>
      notification.notification_type === type,
  );
}

/**
 * Payment failed takes priority over an expiring card
 */
export function useSubscriptionPaymentSeverity():
  | 'caution'
  | 'critical'
  | undefined {
  const paymentFailed = useNotification('subscription_payment_failed');
  const cardExpiring = useNotification('subscription_card_expiring');

  if (paymentFailed) {
    return 'critical';
  }

  if (cardExpiring) {
    return 'caution';
  }

  return undefined;
}

/**
 * Hides subscription payment notifications while
 * `ctms_subscription_payment_notifications` is disabled
 */
export function useSubscriptionPaymentNotificationsSelect() {
  const isEnabled = useFlag('ctms_subscription_payment_notifications');

  return isEnabled ? undefined : omitSubscriptionPaymentNotifications;
}

function omitSubscriptionPaymentNotifications(
  notifications: NotificationVariant[],
): NotificationVariant[] {
  return notifications.filter(
    ({ notification_type }) =>
      !SUBSCRIPTION_PAYMENT_NOTIFICATIONS.has(notification_type),
  );
}
